import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export function KPIWidget({
  title,
  value,
  subtext,
  trend,
  color,
  icon,
}: {
  title: string;
  value: string;
  subtext: string;
  trend: 'up' | 'down';
  color: 'primary' | 'success' | 'warning' | 'error' | 'secondary' | 'magenta';
  icon: React.ReactNode;
}) {
  const colorMap = {
    primary: {
      bg: 'bg-[#00758d]/10',
      text: 'text-[#00758d]',
      border: 'border-[#00758d]/20',
      valueClass: 'text-slate-900',
    },
    secondary: {
      bg: 'bg-[#00303c]/10',
      text: 'text-[#00303c]',
      border: 'border-[#00303c]/20',
      valueClass: 'text-slate-900',
    },
    success: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      valueClass: 'text-emerald-950',
    },
    warning: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', valueClass: 'text-amber-950' },
    error: { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200', valueClass: 'text-rose-950' },
    magenta: {
      bg: 'bg-fuchsia-100',
      text: 'text-fuchsia-700',
      border: 'border-fuchsia-200',
      valueClass: 'text-fuchsia-950',
    },
  };

  const c = colorMap[color];
  const TrendIcon = trend === 'up' ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex items-center justify-between">
          <span className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
          <div
            className={cn('p-2 sm:p-2.5 rounded-lg flex items-center justify-center border', c.bg, c.text, c.border)}
          >
            {icon}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
          <span className={cn('text-2xl sm:text-3xl font-bold tracking-tight font-mono', c.valueClass)}>{value}</span>
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <span
              className={cn(
                'flex items-center pr-1.5 py-0.5 rounded-full',
                trend === 'up' ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold',
              )}
            >
              <TrendIcon className="w-3.5 h-3.5 mr-0.5" />
              {subtext}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
